import { BALANCE } from '../balance.ts';
import { activePuzzleIds, currentRoom, isLooped, isTunnel } from '../game/core.ts';
import { puzzlePlayers } from '../game/context.ts';
import { nominalTimer, timerRatePermille } from '../game/execution.ts';
import { estimatePlanMs, roomEstimateMs } from '../game/planning.ts';
import { getMap } from '../maps/graph.ts';
import type { MapDef, RoomKind } from '../maps/types.ts';
import { PUZZLES } from '../puzzles/registry.ts';
import type {
  Difficulty,
  EntranceId,
  GameEvent,
  GameResult,
  GameState,
  HideoutStage,
  Language,
  MapId,
  Phase,
  PlanSubstage,
  Plan,
  PlayerId,
  Powers,
  PuzzleType,
  Role,
  SabotageKind,
  Shares,
  Stop,
} from '../types.ts';

/**
 * What one player is allowed to know. Built from scratch with an allow-list: nothing is
 * copied from the state and then removed, so a new secret field in `GameState` can never leak
 * by accident. `tests/views.test.ts` plants markers in every secret and checks that they never
 * appear in a view that should not contain them.
 */

export interface ViewEvent {
  id: number;
  type: string;
  atMs: number;
  data: Record<string, unknown>;
  /** Only present once the game reached the reveal. */
  secret?: Record<string, unknown>;
}

export interface MapLayers {
  /** Which puzzle types can appear in each room, as far as this role knows. */
  rooms: Record<string, PuzzleType[]>;
  /** Corridors with guards (lookout and mastermind). */
  guardedCorridors: [string, string][] | null;
  /** Rough minutes-ish cost of a room in ms (mastermind). */
  estimateMs: Record<string, number> | null;
}

export interface PlayerView {
  you: {
    id: PlayerId;
    role: Role;
    /** Only ever `true` for the infiltrator themself; everyone else sees `false`. */
    isInfiltrator: boolean;
    /** Only for the infiltrator. */
    sabotagesLeft: Record<SabotageKind, boolean> | null;
    powers: Powers;
  };
  game: {
    phase: Phase;
    clockMs: number;
    phaseStartedMs: number;
    mapId: MapId;
    difficulty: Difficulty;
    language: Language;
    playerCount: number;
    /** The target's base loot, in millions of lire: public from the briefing on. */
    baseLoot: number;
    /** The execution timer this heist is played against, before the entrance's own cost. */
    timerMs: number;
  };
  players: {
    id: PlayerId;
    name: string;
    role: Role;
    seat: number;
    expelled: boolean;
    pin: PlayerId | null;
  }[];
  briefing: { ready: PlayerId[]; deadlineMs: number } | null;
  planning: {
    substage: PlanSubstage;
    rejections: number;
    proposal: Plan | null;
    votes: Record<PlayerId, boolean>;
    approved: Plan | null;
    deadlineMs: number;
    map: MapPublic;
    layers: MapLayers;
    estimateMs: number | null;
  } | null;
  exec: ExecView | null;
  hideout: HideoutView | null;
  result: GameResult | null;
  events: ViewEvent[];
  /** From the reveal on: every notice of the heist with what really caused it. */
  timeline: ViewEvent[];
}

export interface MapPublic {
  id: MapId;
  baseLoot: number;
  rooms: { id: string; floor: number; kind: RoomKind; extraLoot: number }[];
  edges: { a: string; b: string; guarded: boolean }[];
  ropeLinks: [string, string][];
  entrances: Record<EntranceId, string>;
  vault: string;
}

export interface StopView {
  kind: Stop['kind'];
  room: string | null;
  from: string | null;
  optional: boolean;
  skipped: boolean;
  status: 'done' | 'active' | 'pending';
}

export interface ExecView {
  /** The building the team is in: the same public shape the plan was drawn on. */
  map: MapPublic;
  timerMs: number;
  ratePermille: number;
  elapsedMs: number;
  alarm: number;
  policeAlerted: boolean;
  optionalClosed: boolean;
  looped: boolean;
  tunnel: boolean;
  gear: string[];
  stops: StopView[];
  stopIndex: number;
  room: string | null;
  completedOptional: string[];
  puzzles: {
    id: string;
    type: PuzzleType;
    room: string;
    solved: boolean;
    view: Record<string, unknown>;
  }[];
  /** Mastermind only. */
  planB: { canUndo: boolean; tenths: number } | null;
  /** While the drone flies: what is in the next room (everyone). */
  drone: { room: string | null; pool: PuzzleType[] } | null;
}

export interface HideoutView {
  stage: HideoutStage;
  stageStartedMs: number;
  deadlineMs: number;
  /** Your own ballots only. */
  myAccusation: PlayerId | 'none' | null;
  voted: PlayerId[];
  expelled: PlayerId | null;
  proposer: PlayerId | null;
  proposal: Shares | null;
  splitVoting: boolean;
  mySplitVote: boolean | null;
  splitVoted: PlayerId[];
  splitRejections: number;
  finalShares: Shares | null;
}

export function mapPublic(map: MapDef): MapPublic {
  return {
    id: map.id,
    baseLoot: map.baseLoot,
    rooms: map.rooms.map((r) => ({
      id: r.id,
      floor: r.floor,
      kind: r.kind,
      extraLoot: r.extraLoot,
    })),
    edges: map.edges.map((e) => ({ a: e.a, b: e.b, guarded: e.guarded })),
    ropeLinks: map.ropeLinks.map(([a, b]) => [a, b]),
    entrances: { ...map.entrances },
    vault: map.vault,
  };
}

/** The blueprint layer a role gets during planning (GDD §3.3); the mastermind gets them all. */
function layersFor(map: MapDef, role: Role): MapLayers {
  const keep = (types: readonly PuzzleType[]): PuzzleType[] => {
    if (role === 'mastermind') return [...types];
    const mine: readonly PuzzleType[] =
      role === 'hacker'
        ? ['cameras', 'panel', 'password']
        : role === 'locksmith'
          ? ['lock', 'safe', 'laser']
          : [];
    return types.filter((t) => mine.includes(t));
  };
  const rooms: Record<string, PuzzleType[]> = {};
  for (const r of map.rooms) rooms[r.id] = keep(r.pool);
  const sees = role === 'mastermind' || role === 'lookout';
  return {
    rooms,
    guardedCorridors: sees ? map.edges.filter((e) => e.guarded).map((e) => [e.a, e.b]) : null,
    estimateMs:
      role === 'mastermind'
        ? Object.fromEntries(map.rooms.map((r) => [r.id, roomEstimateMs(map, r.id)]))
        : null,
  };
}

function stopView(st: Stop, index: number, current: number): StopView {
  const status = index < current ? 'done' : index === current ? 'active' : 'pending';
  return {
    kind: st.kind,
    room: st.kind === 'room' ? st.room : st.to,
    from: st.kind === 'room' ? null : st.from,
    optional: (st.kind === 'room' || st.kind === 'corridor') && st.optional,
    skipped: 'skipped' in st ? st.skipped : false,
    status,
  };
}

function eventsFor(s: GameState, pid: PlayerId, revealed: boolean): ViewEvent[] {
  return s.log
    .filter((ev: GameEvent) => ev.audience === 'all' || ev.audience.includes(pid))
    .slice(-120)
    .map((ev) => ({
      id: ev.id,
      type: ev.type,
      atMs: ev.atMs,
      data: ev.data,
      ...(revealed && ev.secret ? { secret: ev.secret } : {}),
    }));
}

export function getPlayerView(state: GameState, playerId: PlayerId): PlayerView {
  const me = state.players.find((p) => p.id === playerId);
  if (!me) throw new Error(`unknown player ${playerId}`);
  const s = state;
  const map = getMap(s.config.mapId);
  const revealed = s.phase === 'reveal' || s.phase === 'ended';
  const amInfiltrator = s.infiltrator.id === playerId;

  const view: PlayerView = {
    you: {
      id: me.id,
      role: me.role,
      isInfiltrator: amInfiltrator,
      sabotagesLeft: amInfiltrator ? { ...s.infiltrator.available } : null,
      powers: { ...me.powers },
    },
    game: {
      phase: s.phase,
      clockMs: s.clockMs,
      phaseStartedMs: s.phaseStartedMs,
      mapId: s.config.mapId,
      difficulty: s.config.difficulty,
      language: s.config.language,
      playerCount: s.players.length,
      baseLoot: map.baseLoot,
      timerMs: nominalTimer(s.config, map),
    },
    players: s.players.map((p) => ({
      id: p.id,
      name: p.name,
      role: p.role,
      seat: p.seat,
      expelled: p.expelled,
      pin: p.pin,
    })),
    briefing:
      s.phase === 'briefing'
        ? { ready: [...s.ready], deadlineMs: s.phaseStartedMs + BALANCE.briefingMs }
        : null,
    planning: null,
    exec: null,
    hideout: null,
    result: revealed && s.result ? structuredClone(s.result) : null,
    events: eventsFor(s, playerId, revealed),
    timeline: revealed
      ? s.log
          .filter((ev) => ev.type === 'notice')
          .map((ev) => ({
            id: ev.id,
            type: ev.type,
            atMs: ev.atMs,
            data: ev.data,
            ...(ev.secret ? { secret: ev.secret } : {}),
          }))
      : [],
  };

  if (s.phase === 'planning') {
    const p = s.planning;
    view.planning = {
      substage: p.substage,
      rejections: p.rejections,
      proposal: p.proposal ? structuredClone(p.proposal) : null,
      votes: { ...p.votes },
      approved: null,
      deadlineMs: s.phaseStartedMs + BALANCE.planningMs,
      map: mapPublic(map),
      layers: layersFor(map, me.role),
      estimateMs: p.proposal ? estimatePlanMs(map, p.proposal) : null,
    };
  }

  const exec = s.exec;
  if (exec && (s.phase === 'execution' || s.phase === 'escape')) {
    const activeIds = new Set(activePuzzleIds(s));
    const ctxPlayers = puzzlePlayers(s);
    const puzzles = Object.values(exec.puzzles)
      .filter((pz) => activeIds.has(pz.id))
      .map((pz) => ({
        id: pz.id,
        type: pz.type,
        room: pz.room,
        solved: pz.solved,
        view: PUZZLES[pz.type].view(pz, {
          playerId,
          role: me.role,
          roles: s.players.map((x) => x.role),
          players: ctxPlayers,
          execMs: exec.elapsedMs,
          alarmTenths: s.alarm,
          tunnel: isTunnel(exec),
          revealed: false,
        }),
      }));
    const last = exec.lastError;
    const next = exec.itinerary[exec.stopIndex + 1];
    view.exec = {
      map: mapPublic(map),
      timerMs: Math.max(0, Math.floor(exec.timerUs / 1000)),
      ratePermille: timerRatePermille(s, exec),
      elapsedMs: exec.elapsedMs,
      alarm: s.alarm,
      policeAlerted: exec.policeAlerted,
      optionalClosed: exec.optionalClosed,
      looped: isLooped(exec),
      tunnel: isTunnel(exec),
      gear: [...exec.gearLeft],
      stops: exec.itinerary.map((st, i) => stopView(st, i, exec.stopIndex)),
      stopIndex: exec.stopIndex,
      room: currentRoom(s),
      completedOptional: [...exec.completedOptional],
      puzzles,
      planB:
        me.role === 'mastermind'
          ? {
              canUndo:
                !!last &&
                !last.used &&
                exec.elapsedMs - last.atMs <= BALANCE.planBWindowMs &&
                me.powers.planB > 0,
              tenths: last && !last.used ? last.tenths : 0,
            }
          : null,
      drone:
        exec.elapsedMs < exec.droneUntilMs && next
          ? {
              room: next.kind === 'room' ? next.room : next.to,
              pool:
                next.kind === 'room'
                  ? [...(map.rooms.find((r) => r.id === next.room)?.pool ?? [])]
                  : ['corridor'],
            }
          : null,
    };
  }

  const h = s.hideout;
  if (h && (s.phase === 'hideout' || revealed)) {
    const myAcc = h.accusations[playerId] ?? null;
    const stageMs =
      h.stage === 'discussion'
        ? BALANCE.discussionMs
        : h.stage === 'accusation'
          ? BALANCE.accusationMs
          : h.splitVoting
            ? BALANCE.splitVoteMs
            : BALANCE.splitProposeMs;
    view.hideout = {
      stage: h.stage,
      stageStartedMs: h.stageStartedMs,
      deadlineMs: h.stageStartedMs + stageMs,
      myAccusation: myAcc,
      voted: Object.keys(h.accusations),
      expelled: h.expelled,
      proposer: h.proposer,
      proposal: h.proposal ? { ...h.proposal } : null,
      splitVoting: h.splitVoting,
      mySplitVote: h.splitVotes[playerId] ?? null,
      splitVoted: Object.keys(h.splitVotes),
      splitRejections: h.splitRejections,
      finalShares: h.finalShares ? { ...h.finalShares } : null,
    };
  }

  return view;
}
